package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.SceneSpec
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.render.effectiveLayers
import dev.lingvo.shared.screenshots.render.effectiveLocaleOverride

/** UI snapshots change at commit boundaries, never for individual pointer samples. */
internal data class StudioUiState(
    val document: ScreenshotDesign,
    val view: HistoryView,
    val selection: Set<String> = emptySet(),
    val canUndo: Boolean = false,
    val canRedo: Boolean = false,
    val zoomPercent: Int = 100,
    val saveLabel: String,
    val readOnlyReason: String? = null,
    val tool: StudioActionType = StudioActionType.SELECT_TOOL,
    val slots: List<StudioSlot> = emptyList(),
    val languages: List<StudioLanguage> = emptyList(),
    val assets: List<StudioAsset> = emptyList(),
    val scenes: List<SceneSpec> = emptyList(),
    val caption: CaptionSnapshot? = null,
    val announcement: String = "",
    val inlineEditing: Boolean = false,
    val captureAvailable: Boolean? = null,
    val error: String? = null,
    val fontStatus: String? = null,
    val fontFailed: Boolean = false,
) {
    val board get() = document.boards.first { it.deviceClass == view.device }
    val page get() = board.pages.first { view.slotId in it.slotIds }
    val layers: List<Layer> get() = effectiveLayers(page, board, view.locale, document.schemaVersion)
    val selected: List<Layer> get() = allLayers(layers).filter { it.base.id in selection }
    val localizedSelection: Boolean get() = effectiveLocaleOverride(page, view.locale, document.schemaVersion)
        ?.patches?.keys?.any { it in selection } == true
    val editable: Boolean get() = readOnlyReason == null
}

/** Status is textual as well as visual; thumbnail URLs are owned and revoked by the thumbnail port. */
internal data class StudioSlot(val id: String, val title: String, val status: String, val thumbnailUrl: String? = null)

/** Caption length and fitted size support finding the hardest language without guessing from locale. */
internal data class StudioLanguage(val locale: String, val label: String, val status: String, val captionLength: Int,
    val fittedSize: Double? = null, val overflow: Boolean = false)

/** Existing captures and project artwork can be added without introducing a user-image upload path. */
internal data class StudioAsset(val id: String, val label: String, val status: String, val source: ImageSource)
